import { Box } from "@mui/material";
import { CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage, SubscriptionContext } from "contexts/Subscription";
import { ReactNode, useContext } from "react";
import { AddOnItemName } from "schemas/dashboard";
import {
  BillingCycle,
  OverridePlanName,
  PLAN_FAMILY,
  Plans,
  getCycleForPlan,
} from "schemas/payments";

import { accessToPartnershipData } from "utils/influencer";

import CustomerSupportAddOn from "./CustomerAddOn";
import EmailAddOn from "./EmailAddOn";
import PartnershipDataBundle from "./PartnershipDataBundle";

const CrossSells = () => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { subscription, addOns } = useContext(SubscriptionContext);
  const { plan, splashPageSource } = useContext(CheckoutContext);

  const family = plan ? PLAN_FAMILY[plan as Plans] : undefined;

  const isMonthly = getCycleForPlan(plan as Plans) === BillingCycle.monthly;
  const isQuarterly = getCycleForPlan(plan as Plans) === BillingCycle.quarterly;

  const hasAccessToPartnershipData = accessToPartnershipData(
    currentOrg,
    subscription,
  );
  const isInCustomerSupportVariant = Boolean(
    addOns?.[AddOnItemName.customer_support],
  );

  // Hide Account Setup when re-subscribing within the same tier family after
  // already buying it — switching to a different tier lets the user repurchase.
  const currentFamily = subscription?.subscription?.planName
    ? PLAN_FAMILY[subscription.subscription.planName]
    : undefined;
  const hasPurchasedCustomerSupport = Boolean(
    subscription?.customerSupportData,
  );
  const displayCustomerSupport =
    isInCustomerSupportVariant &&
    !(hasPurchasedCustomerSupport && currentFamily === family);

  const displayVaBundle =
    family === OverridePlanName.VA &&
    splashPageSource !== SplashPage.va_bundled;
  const displayProBundle =
    family === OverridePlanName.PRO &&
    splashPageSource !== SplashPage.pro_bundle;
  const displayAutopilotBundle = family === OverridePlanName.AUTOPILOT;

  const proEmailVolume =
    isMonthly || isQuarterly
      ? AddOnItemName.email_volume_pro_monthly
      : AddOnItemName.email_volume_pro_yearly;
  const vaEmailVolume =
    isMonthly || isQuarterly
      ? AddOnItemName.email_volume_monthly
      : AddOnItemName.email_volume_yearly;

  const cards: ReactNode[] = [];
  if (displayVaBundle) {
    if (displayCustomerSupport) {
      cards.push(<CustomerSupportAddOn key="customer_support" />);
    }
    cards.push(<EmailAddOn key="email" addOnItemName={vaEmailVolume} />);
  } else if (displayProBundle) {
    if (displayCustomerSupport) {
      cards.push(<CustomerSupportAddOn key="customer_support" />);
    }
    cards.push(<EmailAddOn key="email" addOnItemName={proEmailVolume} />);
    if (!hasAccessToPartnershipData) {
      cards.push(<PartnershipDataBundle key="partnership_data" />);
    }
  } else if (displayAutopilotBundle) {
    if (displayCustomerSupport) {
      cards.push(<CustomerSupportAddOn key="customer_support" />);
    }
    cards.push(<EmailAddOn key="email" addOnItemName={proEmailVolume} />);
  }

  if (cards.length === 0) return null;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1.75,
          fontWeight: 600,
          fontSize: 15,
          color: "#151e16",
        }}
      >
        <Box
          component="i"
          className="fa-solid fa-circle-plus"
          sx={{ color: "#014638", fontSize: 18 }}
        />{" "}
        Enhance your plan
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {cards}
      </Box>
    </Box>
  );
};

export default CrossSells;
